import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useSetAtom } from 'jotai'
import { Center, Loader, Text, Stack } from '@mantine/core'
import { useGameSession } from '../hooks/useGameSession'
import { useBeatHistory } from '../hooks/useBeatHistory'
import { lastPlayedStoryAtom } from '../atoms/playerAtoms'
import { GameScreen } from '../components/game/GameScreen'
import { GameScreen2 } from '../components/game2/GameScreen2'
import { StoryCompleteScreen } from '../components/game/StoryCompleteScreen'
import { SessionWaitingScreen } from '../components/game/SessionWaitingScreen'
import { RoleSelectScreen } from '../components/game/RoleSelectScreen'

export default function GamePlayer() {
  // Two routes render this component: /play/:storyId (live play) and
  // /review/:sessionId (read-only walk-through of a completed story). The
  // presence of sessionId switches the hook into review mode, which loads the
  // completed session by id and suppresses every mutation.
  const { storyId, sessionId } = useParams()
  const reviewMode = !!sessionId
  const session = useGameSession(storyId, reviewMode ? { reviewSessionId: sessionId } : undefined)

  // Remember the story being played so account pages (Profile/Settings) can
  // offer a "Resume story" link back here. Not cleared on unmount — we want it
  // to persist while the player browses settings; logout clears it. Skipped in
  // review — reviewing a finished story shouldn't offer to "resume" it.
  const setLastPlayedStory = useSetAtom(lastPlayedStoryAtom)
  useEffect(() => {
    if (!reviewMode && session.story?.id) {
      setLastPlayedStory({ id: session.story.id, name: session.story.name })
    }
  }, [reviewMode, session.story?.id, session.story?.name, setLastPlayedStory])

  const isGameActive =
    !session.isLoading &&
    !session.isSessionComplete &&
    !session.isWaitingForRelease &&
    !!session.currentBeat

  const { markBackward } = useBeatHistory({
    canGoPrev: session.canGoPrev,
    onPrev: session.handlePrev,
    currentIndex: session.currentIndex,
    currentThreadIndex: session.currentThreadIndex,
    isActive: isGameActive,
  })

  const handlePrevWithHistory = useCallback(() => {
    markBackward()
    session.handlePrev()
  }, [markBackward, session.handlePrev])

  if (session.isLoading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Stack align="center" gap="md">
          <Loader size="lg" color="cyan" />
          <Text c="dimmed" ff="Orbitron, sans-serif" size="sm">
            Loading story...
          </Text>
        </Stack>
      </Center>
    )
  }

  if (session.needsRoleSelection && !session.isSessionComplete) {
    return (
      <RoleSelectScreen
        story={session.story}
        roles={session.playerRoles}
        takenRoleKeys={session.takenRoleKeys}
        onSelect={session.selectRole}
        saving={session.roleSaving}
      />
    )
  }

  if (session.isWaitingForRelease) {
    return (
      <SessionWaitingScreen
        story={session.story}
        releasedCount={session.allThreads?.length}
        totalCount={session.story?.threads_count}
      />
    )
  }

  if (session.isSessionComplete) {
    return (
      <StoryCompleteScreen
        story={session.story}
        serverSessionId={session.serverSessionId}
        onPlayAgain={session.handleReset}
      />
    )
  }

  // Shared prop contract for both player screens. The legacy GameScreen and the
  // new GameScreen2 take the identical props, so the only difference at this
  // layer is which component renders.
  const gameScreenProps = {
    story: session.story,
    sessionRole: session.sessionRole,
    isTeamPlay: session.isTeamPlay,
    teamName: session.teamName,
    playerRoles: session.playerRoles,
    serverSessionId: session.serverSessionId,
    currentBeat: session.currentBeat,
    currentThread: session.currentThread,
    allThreads: session.allThreads,
    currentThreadIndex: session.currentThreadIndex,
    showTitleCard: session.showTitleCard,
    onTitleCardNext: session.onTitleCardNext,
    progress: session.progress,
    overallProgress: session.overallProgress,
    selectedChoice: session.selectedChoice,
    inputValue: session.inputValue,
    generatedContent: session.generatedContent,
    isLoading: false,
    beatsLoading: session.beatsLoading,
    canGoNext: session.canGoNext,
    onNext: session.handleNext,
    onChoiceSelect: session.handleChoiceSelect,
    onInputSubmit: session.handleInputSubmit,
    onInputChange: session.setInputValue,
    canGoPrev: session.canGoPrev,
    onPrev: handlePrevWithHistory,
    isCurrentBeatLocked: session.isCurrentBeatLocked,
    committedChoices: session.committedChoices,
    beats: session.beats,
    currentIndex: session.currentIndex,
    liveThreadIndex: session.liveThreadIndex,
    liveBeatIndex: session.liveBeatIndex,
    isPositionReachable: session.isPositionReachable,
    jumpToPosition: session.jumpToPosition,
    skipToCurrent: session.skipToCurrent,
    reviewMode: session.reviewMode,
  }

  // Review always uses the redesigned player — the Story Map (BeatTimeline)
  // free-navigation the review feature is built around lives in GameScreen2.
  // For live play the backend resolves the per-story flag OR the per-user
  // preview allowlist into a single boolean; everyone else stays on the current
  // player.
  if (reviewMode || session.story?.use_new_interface) {
    return <GameScreen2 {...gameScreenProps} />
  }

  return <GameScreen {...gameScreenProps} />
}
